<template>
    <main class="dashboard panel-analista">
        <MainLayout
            :chartLoaded="true"
            @change-section="onSectionChange"
        />

        <div class="main-section underHeader">
            <div class="panel-shell">
                <!-- Encabezado: contexto + acciones. El analista llega acá para
                     ver de un vistazo qué clientes necesitan atención. -->
                <header class="panel-head">
                    <div class="panel-head__intro">
                        <p class="panel-eyebrow">Panel analista</p>
                        <h1 class="panel-title">Cartera de clientes</h1>
                        <p class="panel-sub">
                            <template v-if="numberRegisters">
                                {{ numberRegisters }} empresas — revisá la salud de datos de cada una.
                            </template>
                            <template v-else>Tus empresas asignadas.</template>
                        </p>
                    </div>
                    <div class="panel-actions">
                        <button type="button" class="pill" @click="asignarAnalistas">
                            Asignar analistas
                        </button>
                        <button type="button" class="pill" @click="agregarCompania">
                            Agregar compañía
                        </button>
                        <button type="button" class="pill" @click="verPlanesEstandar">
                            Planes estándar
                        </button>
                        <button type="button" class="pill" @click="verDesfasesFintoc">
                            Desfases Fintoc
                        </button>
                        <button type="button" class="pill pill--accent" @click="verSolicitudes">
                            Solicitudes
                            <span v-if="pendingRequestsCount" class="pill__badge">
                                {{ pendingRequestsCount }}
                            </span>
                        </button>
                    </div>
                </header>

                <!-- Cartera -->
                <div class="panel-card">
                    <div class="cartera-toolbar">
                        <div class="search-box">
                            <input
                                v-model="searchQuery"
                                type="search"
                                class="search-box__input"
                                placeholder="Buscar por empresa, RUT o usuario…"
                                aria-label="Buscar en la cartera"
                            />
                            <button
                                v-if="searchQuery"
                                type="button"
                                class="search-box__clear"
                                aria-label="Limpiar búsqueda"
                                @click="searchQuery = ''"
                            >×</button>
                        </div>
                        <span class="toolbar-divider" aria-hidden="true"></span>

                        <!-- Filtros: un control por pregunta. Los conteos por
                             opción salen de la cartera completa (no del
                             resultado filtrado): son el mapa, no el recorte. -->
                        <b-dropdown v-model="filterAnalyst" aria-role="list" class="fdrop">
                            <template #trigger>
                                <button
                                    type="button"
                                    class="fpill"
                                    :class="{ 'is-active': filterAnalyst !== null }"
                                >
                                    {{ analystTriggerLabel }}
                                    <span class="fpill__caret" aria-hidden="true">▾</span>
                                </button>
                            </template>
                            <b-dropdown-item :value="null" aria-role="listitem">
                                <span class="fopt">
                                    <span class="fopt__label">Todos los analistas</span>
                                    <span class="fopt__count">{{ carteraClientes.length }}</span>
                                </span>
                            </b-dropdown-item>
                            <b-dropdown-item value="__me__" aria-role="listitem">
                                <span class="fopt">
                                    <span class="fopt__label">Mi cartera</span>
                                    <span class="fopt__count">{{ myCompaniesCount }}</span>
                                </span>
                            </b-dropdown-item>
                            <b-dropdown-item v-if="analystOptions.length" separator />
                            <b-dropdown-item
                                v-for="opt in analystOptions"
                                :key="opt.value"
                                :value="opt.value"
                                aria-role="listitem"
                            >
                                <span class="fopt">
                                    <span class="fopt__label">{{ opt.label }}</span>
                                    <span class="fopt__count">{{ opt.count }}</span>
                                </span>
                            </b-dropdown-item>
                        </b-dropdown>

                        <!-- El dropdown ES la leyenda del semáforo de la columna
                             Estado de datos: mismos puntos, mismos colores. -->
                        <b-dropdown v-model="filterSii" aria-role="list" class="fdrop">
                            <template #trigger>
                                <button
                                    type="button"
                                    class="fpill"
                                    :class="{ 'is-active': filterSii !== null }"
                                >
                                    {{ siiTriggerLabel }}
                                    <span class="fpill__caret" aria-hidden="true">▾</span>
                                </button>
                            </template>
                            <b-dropdown-item :value="null" aria-role="listitem">
                                <span class="fopt">
                                    <span class="fopt__label">Todos los estados</span>
                                    <span class="fopt__count">{{ carteraClientes.length }}</span>
                                </span>
                            </b-dropdown-item>
                            <b-dropdown-item value="__attention__" aria-role="listitem">
                                <span class="fopt">
                                    <span class="fopt__label">
                                        <span class="fopt__dot is-warn"></span>
                                        Necesitan atención
                                    </span>
                                    <span class="fopt__count">{{ siiAttentionCount }}</span>
                                </span>
                            </b-dropdown-item>
                            <b-dropdown-item separator />
                            <b-dropdown-item
                                v-for="opt in siiOptions"
                                :key="opt.value"
                                :value="opt.value"
                                aria-role="listitem"
                            >
                                <span class="fopt">
                                    <span class="fopt__label">
                                        <span class="fopt__dot" :class="opt.dot"></span>
                                        {{ opt.label }}
                                    </span>
                                    <span class="fopt__count">{{ opt.count }}</span>
                                </span>
                            </b-dropdown-item>
                        </b-dropdown>

                        <button
                            v-if="noPlanCount"
                            type="button"
                            class="fpill"
                            :class="{ 'is-active': filterNoPlan }"
                            @click="filterNoPlan = !filterNoPlan"
                        >
                            Sin plan de cuentas
                            <span class="fpill__count">{{ noPlanCount }}</span>
                        </button>
                        <span class="cartera-toolbar__spacer" aria-hidden="true"></span>
                        <button
                            v-if="hasActiveFilters"
                            type="button"
                            class="toolbar-clear"
                            @click="clearFilters"
                        >
                            Limpiar filtros
                        </button>
                        <span v-if="searchQuery || hasActiveFilters" class="cartera-toolbar__count">
                            {{ filteredData.length }} de {{ carteraClientes.length }} empresas
                        </span>
                    </div>
                    <b-table
                        :data="sortedData"
                        :loading="tableData.loading"
                        ref="table"
                        :total="numberRegisters"
                        :show-detail-icon="false"
                        class="cartera-table"
                    >
                        <template #empty>
                            <div class="cartera-empty">
                                <template v-if="searchQuery || hasActiveFilters">
                                    Sin resultados con la búsqueda y filtros actuales.
                                    <button
                                        v-if="hasActiveFilters"
                                        type="button"
                                        class="toolbar-clear"
                                        @click="clearFilters"
                                    >
                                        Limpiar filtros
                                    </button>
                                </template>
                                <template v-else>
                                    No hay clientes en tu cartera todavía.
                                </template>
                            </div>
                        </template>

                        <b-table-column field="company_name" cell-class="col col--company">
                            <template #header>
                                <button class="col-head" @click="changeSortCompanyName">
                                    Compañía <span class="col-head__sort">{{ orderMsg['company_name'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <button
                                    class="company-link"
                                    :class="{ 'is-mine': row.username === analystName }"
                                    @click="openCompany(row)"
                                >
                                    {{ row.company_name }}
                                </button>
                            </template>
                        </b-table-column>

                        <b-table-column field="company_type" cell-class="col col--type">
                            <template #header>
                                <button class="col-head" @click="changeSortCompanyType">
                                    Tipo <span class="col-head__sort">{{ orderMsg['company_type'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <span class="type-tag">{{ row.company_type || '—' }}</span>
                            </template>
                        </b-table-column>

                        <b-table-column field="username" cell-class="col col--analyst">
                            <template #header>
                                <button class="col-head" @click="changeSortAnalista">
                                    Analista <span class="col-head__sort">{{ orderMsg['nombre'] }}</span>
                                </button>
                            </template>
                            <template #default="{ row }">
                                <span class="analyst" :class="{ 'is-mine': row.username === analystName }">
                                    {{ row.nombre }}
                                </span>
                            </template>
                        </b-table-column>

                        <!-- Firma del panel: la "tira de salud" — pills semánticos
                             que se leen de un vistazo por columna. Sin sort: el
                             viejo ordenaba por completion (subida de archivos
                             abandonada, siempre 0). -->
                        <b-table-column field="health" cell-class="col col--health">
                            <template #header>
                                <span class="col-head col-head--static">Estado de datos</span>
                            </template>
                            <template #default="{ row }">
                                <div class="health">
                                    <!-- Semáforo SII: 5 estados derivados en el backend
                                         (sii_status_selector); el detalle viaja en el title. -->
                                    <span
                                        class="chip"
                                        :class="siiChip(row).cls"
                                        :title="row.company_sii_detail || ''"
                                    >
                                        <span class="chip__dot"></span> {{ siiChip(row).label }}
                                    </span>
                                    <span class="chip" :class="row.company_has_account_plans ? 'is-ok' : 'is-off'">
                                        <span class="chip__dot"></span> {{ row.company_n_account_plans }} plan de cuentas
                                    </span>
                                    <span class="chip" :class="row.company_has_categories ? 'is-ok' : 'is-off'">
                                        <span class="chip__dot"></span> {{ row.company_n_categories }} categorías
                                    </span>
                                    <!-- Chat Phi: morado, solo para empresas con usuarios
                                         con el chatbot activado; los emails viajan en el
                                         tooltip (floating-vue, un email por línea). -->
                                    <span
                                        v-if="phiEmails(row).length"
                                        v-tooltip="{ content: phiEmails(row).join('\n'), theme: 'email-list' }"
                                        class="chip is-phi is-clickable"
                                    >
                                        <span class="chip__dot"></span> Chat Phi ({{ phiEmails(row).length }})
                                    </span>
                                </div>
                            </template>
                        </b-table-column>

                    </b-table>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import MainLayout from "@/components/layout/MainLayout";
import AddCompany from "@/components/modals/AddCompany";
import AssignAnalysts from "@/components/modals/AssignAnalysts";
import LinkRequestsQueue from "@/components/modals/LinkRequestsQueue";

import { mapActions } from "vuex";

import { consoleHandlerError, notificate } from "@/utils/message";

// Estados del semáforo SII para el dropdown-leyenda: mismo orden, etiquetas y
// colores de punto que la columna Estado de datos (siiChip).
const SII_FILTER_OPTIONS = [
    { value: "OK", label: "Al día", dot: "is-ok" },
    { value: "CONNECTING", label: "Conectando", dot: "is-progress" },
    { value: "LIKELY_BROKEN", label: "A revisar", dot: "is-warn" },
    { value: "BROKEN", label: "Con problemas", dot: "is-bad" },
    { value: "NOT_APPLICABLE", label: "Sin SII", dot: "is-off" },
];
// "Necesitan atención" = el atajo del analista (reemplaza el pill del header).
const SII_ATTENTION = ["LIKELY_BROKEN", "BROKEN"];

export default {
    name: "PanelAnalista",
    components: {
        MainLayout,
    },
    data() {
        return {
            pendingRequestsCount: 0,
            // Filtros del riel de la toolbar. Analista: null | "__me__" | nombre.
            // SII: null | "__attention__" | estado del semáforo.
            filterAnalyst: null,
            filterSii: null,
            filterNoPlan: false,
            searchQuery: "",
            sortCycle: 0,
            analystName: '',
            carteraClientes: [],
            tableData: {
                loading: true,
                page: 0,
            },
            numberRegisters: 0,
            sortColumn: null,
            sortDirection: null,
            orderMsg: {
                'nombre': '↕',
                'company_rut': '↕',
                'company_name': '↕',
                'bigger_email': '↕',
                'company_type': '↕',
            },
            modalDefaultUpdateSheet: {
                parent: this,
                hasModalCard: true,
                customClass: "modal-class",
                canCancel: ["escape", "outside"],
            },
        };
    },
    mounted() {
        this.loadData();
        this.loadPendingRequestsCount();
    },
    computed: {
        myCompaniesCount() {
            return this.carteraClientes.filter(
                (row) => row.username === this.analystName
            ).length;
        },
        // Resto de los analistas (yo ya tengo "Mi cartera"), con su conteo.
        analystOptions() {
            const counts = new Map();
            for (const row of this.carteraClientes) {
                if (row.username === this.analystName) continue;
                const current = counts.get(row.nombre) || 0;
                counts.set(row.nombre, current + 1);
            }
            return [...counts.entries()]
                .map(([label, count]) => ({ value: label, label, count }))
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        siiCounts() {
            const counts = {};
            for (const row of this.carteraClientes) {
                const status = this.siiStatusOf(row);
                counts[status] = (counts[status] || 0) + 1;
            }
            return counts;
        },
        siiAttentionCount() {
            return SII_ATTENTION.reduce(
                (total, status) => total + (this.siiCounts[status] || 0),
                0
            );
        },
        // Solo los estados presentes en la cartera: el dropdown es leyenda de
        // lo que hay, no catálogo de lo posible.
        siiOptions() {
            return SII_FILTER_OPTIONS
                .map((opt) => ({ ...opt, count: this.siiCounts[opt.value] || 0 }))
                .filter((opt) => opt.count > 0);
        },
        noPlanCount() {
            return this.carteraClientes.filter(
                (row) => !row.company_has_account_plans
            ).length;
        },
        hasActiveFilters() {
            return (
                this.filterAnalyst !== null ||
                this.filterSii !== null ||
                this.filterNoPlan
            );
        },
        analystTriggerLabel() {
            if (this.filterAnalyst === null) return "Analista";
            if (this.filterAnalyst === "__me__") return "Mi cartera";
            return this.filterAnalyst;
        },
        siiTriggerLabel() {
            if (this.filterSii === null) return "Estado SII";
            if (this.filterSii === "__attention__") return "SII: atención";
            const opt = SII_FILTER_OPTIONS.find(
                (candidate) => candidate.value === this.filterSii
            );
            return opt ? `SII: ${opt.label.toLowerCase()}` : "Estado SII";
        },
        searchedData() {
            // Case- y tilde-insensitive: "panaderia" matchea "Panadería".
            const fold = (value) =>
                String(value || "")
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");
            const query = fold(this.searchQuery.trim());
            if (!query) {
                return this.carteraClientes;
            }
            // El RUT se compara sin puntos/guiones/espacios en ambos lados:
            // "90.002.865-2", "90002865-2" y "90002865" matchean igual.
            const rutQuery = query.replace(/[.\s-]/g, "");
            return this.carteraClientes.filter((row) => {
                const textFields = [
                    row.company_name,
                    row.bigger_email,
                    row.company_contact,
                    row.nombre,
                    row.username,
                ];
                if (textFields.some((value) => fold(value).includes(query))) {
                    return true;
                }
                const rut = fold(row.company_rut).replace(/[.\s-]/g, "");
                return Boolean(rutQuery) && rut.includes(rutQuery);
            });
        },
        filteredData() {
            let rows = this.searchedData;
            if (this.filterAnalyst === "__me__") {
                rows = rows.filter((row) => row.username === this.analystName);
            } else if (this.filterAnalyst !== null) {
                rows = rows.filter((row) => row.nombre === this.filterAnalyst);
            }
            if (this.filterSii === "__attention__") {
                rows = rows.filter((row) =>
                    SII_ATTENTION.includes(this.siiStatusOf(row))
                );
            } else if (this.filterSii !== null) {
                rows = rows.filter(
                    (row) => this.siiStatusOf(row) === this.filterSii
                );
            }
            if (this.filterNoPlan) {
                rows = rows.filter((row) => !row.company_has_account_plans);
            }
            return rows;
        },
        sortedData() {
            if (!this.sortColumn) {
                return this.filteredData;
            }
            return [...this.filteredData].sort((a, b) => {
                var aval = a[this.sortColumn];
                var bval = b[this.sortColumn];
                aval = String(aval).toLowerCase();
                bval = String(bval).toLowerCase();
                if (aval < bval) {
                    return this.sortDirection === 'asc' ? -1 : 1;
                }
                if (aval > bval) {
                    return this.sortDirection === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
    },
    methods: {
        ...mapActions("analystCompany", ["fetchCartera", "fetchLinkRequests"]),
        // Estado SII normalizado: company_sii_status del backend, con fallback
        // al booleano legacy company_has_sii_fintoc si el payload viejo sigue
        // cacheado. Fuente única para el chip de la columna y los filtros.
        siiStatusOf(row) {
            if (row.company_sii_status) return row.company_sii_status;
            return row.company_has_sii_fintoc ? "OK" : "NOT_APPLICABLE";
        },
        siiChip(row) {
            const chips = {
                OK: { cls: "is-ok", label: "SII ok" },
                CONNECTING: { cls: "is-progress", label: "SII conectando" },
                LIKELY_BROKEN: { cls: "is-warn", label: "SII a revisar" },
                BROKEN: { cls: "is-bad", label: "SII con problemas" },
                NOT_APPLICABLE: { cls: "is-off", label: "Sin SII" },
            };
            return chips[this.siiStatusOf(row)];
        },
        phiEmails(row) {
            return row.company_ai_chat_emails || [];
        },
        clearFilters() {
            this.filterAnalyst = null;
            this.filterSii = null;
            this.filterNoPlan = false;
        },
        loadData() {
            this.tableData.loading = true;
            // Se retorna la chain (post-catch: nunca rechaza) para que los
            // tests puedan await-earla; los callers la ignoran.
            return this.fetchCartera().then((data) => {
                this.analystName = data.analystName;
                this.numberRegisters = data.count;
                // get_all ya trae el enriquecimiento por-compañía (tipo,
                // contacto); no hay que pedir complementary_data por fila.
                // Reemplazo (no push): los modales recargan al cerrar y el
                // push duplicaba las filas en cada recarga.
                this.carteraClientes = [...data.objects];
            }).catch((error) => {
                // silent: el modo default de consoleHandlerError re-lanza, y
                // acá nadie más consume la promise — volvería a ser una
                // unhandled rejection (CFO_REMOTO_FRONTEND-9S).
                consoleHandlerError({ error, prefix: "[panelAnalista]", silent: true });
                notificate(
                    "No pudimos cargar tu cartera de clientes. Revisá tu conexión e intentá de nuevo.",
                    "is-danger is-light"
                );
            }).finally(() => {
                this.tableData.loading = false;
            });
        },
        agregarCompania() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: AddCompany,
                events: {
                    close: () => {
                        this.loadData();
                    }
                },
                onCancel: () => {
                    this.loadData();
                },
                onClose: () => {
                    this.loadData();
                }
            });
        },
        asignarAnalistas() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: AssignAnalysts,
                props: {
                    currentAnalystEmail: this.analystName,
                },
                events: {
                    close: () => {
                        this.loadData();
                    }
                },
                onCancel: () => {
                    this.loadData();
                },
                onClose: () => {
                    this.loadData();
                },
            });
        },
        verPlanesEstandar() {
            this.$router.push({ name: "standardAccountPlans" });
        },
        verDesfasesFintoc() {
            this.$router.push({ name: "fintocMismatches" });
        },
        loadPendingRequestsCount() {
            this.fetchLinkRequests().then((data) => {
                this.pendingRequestsCount = data.requests.length;
            }).catch(() => {
                this.pendingRequestsCount = 0;
            });
        },
        verSolicitudes() {
            this.$buefy.modal.open({
                ...this.modalDefaultUpdateSheet,
                component: LinkRequestsQueue,
                events: {
                    close: () => {
                        this.loadPendingRequestsCount();
                    }
                },
                onCancel: () => {
                    this.loadPendingRequestsCount();
                },
                onClose: () => {
                    this.loadPendingRequestsCount();
                },
            });
        },
        resetMsgs() {
            for (var key in this.orderMsg) {
                this.orderMsg[key] = '↕';
            }
        },
        changeOrderGeneric(column) {
            // Ciclo de orden en la instancia (antes era un `let count` a nivel
            // módulo: compartido entre remounts, se desincronizaba de
            // sortColumn al volver a la vista).
            if (this.sortColumn != column) {
                this.sortCycle = 0;
                this.sortColumn = column;
            }
            this.sortCycle = (this.sortCycle + 1) % 3;
            this.resetMsgs();
            if (this.sortCycle == 0) {
                this.sortColumn = null;
                this.orderMsg[column] = '↕'
            } else if (this.sortCycle == 1) {
                this.sortColumn = column;
                this.sortDirection = 'asc';
                this.orderMsg[column] = '↑';
            } else if (this.sortCycle == 2) {
                this.sortColumn = column;
                this.sortDirection = 'desc';
                this.orderMsg[column] = '↓';
            }
        },
        changeSortAnalista() {
            this.changeOrderGeneric('nombre');
        },
        changeSortCompanyName() {
            this.changeOrderGeneric('company_name');
        },
        changeSortCompanyType() {
            this.changeOrderGeneric('company_type');
        },
        openCompany(companySelected) {
            this.$router.push({
                name: "companyData",
                query: {
                    companyId: companySelected.company_id,
                    companyRut: companySelected.company_rut
                },
            })
        },
        onSectionChange(section, tab) {
            // PanelAnalista no es una sección del sidebar — cualquier click en el
            // rail navega al Dashboard con el query correspondiente.
            const query = { section };
            if (tab) query.tab = tab;
            this.$router.push({ path: "/dashboard", query }).catch((err) => {
                if (err.name !== "NavigationDuplicated") throw err;
            });
        },
    },
}
</script>

<style scoped lang="scss">
// ============================================================================
// Panel Analista — acotado a esta vista (NO toca Bulma global). Consume el tema
// compartido del analista (var(--ap-*), en elements/_analyst_theme.scss) para
// que el panel y sus modales usen la MISMA paleta/tipografía. Tipografía del
// proyecto (Inter / fallback Open Sans). Look de Bulma sobrescrito en scope.
// ============================================================================
.panel-analista {
    font-family: var(--ap-font);
    color: var(--ap-ink-2);
}

.panel-shell {
    max-width: 1240px;
    margin: 0 auto;
    padding: 32px 28px 56px;
}

// Encabezado ----------------------------------------------------------------
.panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 24px;
}

.panel-eyebrow {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ap-teal);
}

.panel-title {
    margin: 0;
    font-family: var(--ap-font);
    font-weight: 700;
    font-size: 30px;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--ap-ink);
}

.panel-sub {
    margin: 8px 0 0;
    font-size: 14px;
    color: var(--ap-slate);
}

.panel-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

// Pills de acción (micro-interacción sutil: eleva 1px y tiñe el borde) --------
.pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-ink-2);
    font-family: var(--ap-font);
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.16s var(--ap-ease), border-color 0.16s var(--ap-ease),
        background-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &:hover {
        transform: translateY(-1px);
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
        box-shadow: 0 6px 18px -10px rgba(14, 47, 52, 0.5);
    }

    &:active {
        transform: translateY(0);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }
}

// Filtro "SII con problemas": pill con estado activo persistente.
.pill--filter {
    &.is-active {
        border-color: var(--ap-alert);
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
    }
}

.pill--accent {
    border-color: transparent;
    background-color: var(--ap-ink);
    color: var(--ap-paper);

    &:hover {
        color: var(--ap-paper);
        background-color: var(--ap-teal-deep);
        border-color: transparent;
    }
}

.pill__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-alert);
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

// Card contenedor de la tabla -----------------------------------------------
.panel-card {
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius);
    box-shadow: var(--ap-shadow-card);
    overflow: hidden;
}

// Toolbar de la cartera: buscador + riel de filtros + contador ----------------
.cartera-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--ap-line);
    flex-wrap: wrap;
}

.toolbar-divider {
    width: 1px;
    height: 22px;
    background-color: var(--ap-line);
    flex-shrink: 0;
}

.cartera-toolbar__spacer {
    flex: 1;
}

// Pills de filtro: versión compacta del .pill del header (32px). El estado
// activo es teal (selección), no alerta — la urgencia la dice el contenido.
.fpill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-paper);
    color: var(--ap-ink-2);
    font-family: var(--ap-font);
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.16s var(--ap-ease),
        background-color 0.16s var(--ap-ease), color 0.16s var(--ap-ease);

    &:hover {
        border-color: var(--ap-teal);
        color: var(--ap-teal-deep);
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }

    &.is-active {
        border-color: var(--ap-teal);
        background-color: var(--ap-teal-wash);
        color: var(--ap-teal-deep);

        .fpill__count {
            background-color: var(--ap-teal);
            color: var(--ap-paper);
        }
    }
}

.fpill__caret {
    font-size: 9px;
    color: var(--ap-muted);
}

.fpill__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-mist);
    color: var(--ap-slate);
    font-size: 10.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.toolbar-clear {
    border: none;
    background: transparent;
    padding: 0 4px;
    font-family: var(--ap-font);
    font-size: 12.5px;
    font-weight: 600;
    color: var(--ap-slate);
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-color: transparent;
    transition: color 0.14s var(--ap-ease),
        text-decoration-color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-teal-deep);
        text-decoration-color: currentColor;
    }

    &:focus-visible {
        outline: 2px solid var(--ap-teal);
        outline-offset: 2px;
    }
}

// Dropdowns del riel: panel de Bulma re-tematizado al sistema del panel.
.fdrop {
    :deep(.dropdown-content) {
        border: 1px solid var(--ap-line);
        border-radius: var(--ap-radius-sm, 10px);
        background-color: var(--ap-paper);
        box-shadow: var(--ap-shadow-card);
        padding: 6px;
        min-width: 220px;
    }

    :deep(.dropdown-item) {
        padding: 7px 10px;
        border-radius: 7px;
        font-family: var(--ap-font);
        font-size: 13px;
        color: var(--ap-ink-2);

        &:hover {
            background-color: var(--ap-mist);
            color: var(--ap-ink);
        }

        &.is-active {
            background-color: var(--ap-teal-wash);
            color: var(--ap-teal-deep);
            font-weight: 600;
        }
    }

    :deep(.dropdown-divider) {
        margin: 6px 4px;
        background-color: var(--ap-line);
    }
}

.fopt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
}

.fopt__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.fopt__count {
    font-size: 11.5px;
    color: var(--ap-muted);
    font-variant-numeric: tabular-nums;
}

// Puntos del semáforo: mismos colores que .chip__dot de la columna.
.fopt__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;

    &.is-ok { background-color: var(--ap-ok); }
    &.is-progress { background-color: var(--ap-teal); }
    &.is-warn { background-color: var(--ap-warn); }
    &.is-bad { background-color: var(--ap-alert); }
    &.is-off { background-color: #c4d0d1; }
}

.search-box {
    position: relative;
    flex: 0 1 340px;
}

.search-box__input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    font-family: var(--ap-font);
    font-size: 14px;
    color: var(--ap-ink-2);
    background-color: var(--ap-paper);
    border: 1px solid var(--ap-line);
    border-radius: var(--ap-radius-pill);
    transition: border-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

    &::placeholder {
        color: var(--ap-ink-3, #98a2b3);
    }

    &:focus {
        outline: none;
        border-color: var(--ap-teal);
        box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.12);
    }
}

.search-box__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--ap-ink-2);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background-color: var(--ap-line);
    }
}

.cartera-toolbar__count {
    font-size: 13px;
    color: var(--ap-ink-2);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

// Reset del cromo de Bulma dentro del scope + look propio de la tabla --------
:deep(.cartera-table){
    font-family: var(--ap-font);

    .table {
        background-color: transparent;
        color: var(--ap-ink-2);
        width: 100%;
    }

    // Cabecera: eyebrow discreto, sin el th default de Bulma.
    thead th {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        background-color: var(--ap-mist);
        padding: 0;
        vertical-align: middle;
    }

    tbody td {
        border: none;
        border-bottom: 1px solid var(--ap-line);
        padding: 16px 18px;
        vertical-align: middle;
        font-size: 13.5px;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    // Micro-interacción: la fila se tiñe al hover (escaneo cómodo).
    tbody tr {
        transition: background-color 0.14s var(--ap-ease);
    }

    tbody tr:hover {
        background-color: var(--ap-teal-wash);
    }
}

// Cabeceras (botón de orden) ------------------------------------------------
:deep(.col-head){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 13px 18px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: var(--ap-font);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ap-muted);
    transition: color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-ink);
    }
}

:deep(.col-head--static){
    cursor: default;

    &:hover {
        color: var(--ap-muted);
    }
}

:deep(.col-head__sort){
    font-size: 12px;
    color: var(--ap-slate);
}

// Anchos por columna (acotados, sin los overrides globales del original) -----
:deep(.col--company){
    width: 22%;
    min-width: 180px;
}

:deep(.col--type){
    width: 8%;
    white-space: nowrap;
}

:deep(.col--analyst){
    width: 14%;
}

:deep(.col--health){
    width: 40%;
    min-width: 260px;
}

// Nombre de empresa: ancla clickeable ---------------------------------------
:deep(.company-link){
    border: none;
    background: transparent;
    padding: 0;
    text-align: left;
    font-family: var(--ap-font);
    font-size: 14px;
    font-weight: 600;
    color: var(--ap-ink);
    cursor: pointer;
    border-bottom: 1px solid transparent;
    transition: color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);

    &:hover {
        color: var(--ap-teal-deep);
        border-bottom-color: rgba(13, 125, 128, 0.4);
    }

    &.is-mine {
        color: var(--ap-teal-deep);
    }
}

// Tipo: tag neutro y disciplinado (la firma está en el estado, no acá) -------
:deep(.type-tag){
    display: inline-block;
    padding: 3px 10px;
    border: 1px solid var(--ap-line-strong);
    border-radius: var(--ap-radius-pill);
    background-color: var(--ap-mist);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--ap-slate);
}

:deep(.analyst){
    color: var(--ap-slate);
    font-size: 13px;

    &.is-mine {
        color: var(--ap-ink);
        font-weight: 600;
    }
}

// FIRMA: tira de salud — pills semánticos que se leen de un vistazo ----------
:deep(.health){
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

:deep(.chip){
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: var(--ap-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
    white-space: nowrap;
    border: 1px solid transparent;
    font-variant-numeric: tabular-nums;

    .chip__dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    &.is-ok {
        background-color: var(--ap-ok-wash);
        color: var(--ap-ok-ink);
        border-color: rgba(15, 157, 107, 0.22);

        .chip__dot {
            background-color: var(--ap-ok);
        }
    }

    &.is-off {
        background-color: var(--ap-mist);
        color: var(--ap-muted);
        border-color: var(--ap-line);

        .chip__dot {
            background-color: #c4d0d1;
        }
    }

    // Semáforo SII: conectando / a revisar / con problemas.
    &.is-progress {
        background-color: var(--ap-teal-wash);
        color: var(--ap-teal-deep);
        border-color: rgba(13, 125, 128, 0.25);

        .chip__dot {
            background-color: var(--ap-teal);
        }
    }

    &.is-warn {
        background-color: var(--ap-warn-wash);
        color: var(--ap-warn-ink);
        border-color: rgba(200, 138, 0, 0.25);

        .chip__dot {
            background-color: var(--ap-warn);
        }
    }

    &.is-bad {
        background-color: var(--ap-alert-wash);
        color: var(--ap-alert);
        border-color: rgba(192, 69, 59, 0.25);

        .chip__dot {
            background-color: var(--ap-alert);
        }
    }

    // Chat Phi: usuarios de la empresa con el chatbot habilitado.
    &.is-phi {
        background-color: var(--ap-phi-wash);
        color: var(--ap-phi-ink);
        border-color: rgba(123, 91, 214, 0.25);

        .chip__dot {
            background-color: var(--ap-phi);
        }
    }
}

.cartera-empty {
    padding: 48px 0;
    text-align: center;
    color: var(--ap-muted);
    font-size: 14px;
}

@media (prefers-reduced-motion: reduce) {
    .pill,
    .fpill,
    .toolbar-clear,
    :deep(.cartera-table tbody tr),
    :deep(.company-link),
    :deep(.col-head){
        transition: none;
    }
}
</style>
